import { logger } from '@automata/utils';
import { SafeParseReturnType, z } from 'zod';

import type { AdditionalDataInput } from '../../../../types';
import { FIELD_VALIDATION_CONSTANTS } from '../../../../zodSchemas/dataFieldInput';

export { FIELD_VALIDATION_CONSTANTS };

type FieldType = 'string' | 'integer' | 'float' | 'boolean';

export type DataFieldFormValues = {
  label: string;
  id: string;
  type: FieldType;
  defaultValue: string;
  options: string;
};

export function sanitizeId(label: string): string {
  return label
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '')
    .replace(/^_+|_+$/g, '');
}

export function getFieldTypeDisplay(field: AdditionalDataInput): string {
  if (field.options && field.options.length > 0) {
    return 'Dropdown';
  }

  switch (field.type) {
    case 'string':
      return 'Text';
    case 'integer':
    case 'float':
      return 'Number';
    case 'boolean':
      return 'Boolean';
    default:
      return 'Text';
  }
}

export function formatOptionsForDisplay(
  options: string[] | number[] | boolean[] | null | undefined
): string {
  if (!options || options.length === 0) {
    return '';
  }
  return options.join(', ');
}

export function parseOptionsFromString(
  optionsString: string,
  fieldType: 'string' | 'integer' | 'float' | 'boolean'
): {
  error: string | null;
  parsedOptions: string[] | number[] | boolean[] | null;
} {
  if (!optionsString || optionsString.trim() === '') {
    return { error: null, parsedOptions: null };
  }

  const optionArray = optionsString
    .split(',')
    .map((opt) => opt.trim())
    .filter((opt) => opt !== '');

  if (optionArray.length === 0) {
    return { error: null, parsedOptions: null };
  }

  switch (fieldType) {
    case 'integer': {
      const parsed: number[] = [];
      for (const opt of optionArray) {
        const num = Number(opt);
        if (isNaN(num) || !Number.isInteger(num)) {
          return {
            error: `"${opt}" is not a valid integer`,
            parsedOptions: null,
          };
        }
        parsed.push(num);
      }
      return { error: null, parsedOptions: parsed };
    }

    case 'float': {
      const parsed: number[] = [];
      for (const opt of optionArray) {
        const num = Number(opt);
        if (isNaN(num)) {
          return {
            error: `"${opt}" is not a valid number`,
            parsedOptions: null,
          };
        }
        parsed.push(num);
      }
      return { error: null, parsedOptions: parsed };
    }

    case 'boolean': {
      const parsed: boolean[] = [];
      for (const opt of optionArray) {
        const lower = opt.toLowerCase();
        if (lower !== 'true' && lower !== 'false') {
          return {
            error: `"${opt}" is not a valid boolean (true/false)`,
            parsedOptions: null,
          };
        }
        parsed.push(lower === 'true');
      }
      return { error: null, parsedOptions: parsed };
    }

    case 'string':
      return { error: null, parsedOptions: optionArray };

    default:
      return { error: null, parsedOptions: null };
  }
}

export function convertDefaultValueToType(
  value: string | number | boolean | null | undefined,
  fieldType: 'string' | 'integer' | 'float' | 'boolean'
): string | number | boolean | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  switch (fieldType) {
    case 'integer':
    case 'float':
      return Number(value);
    case 'boolean':
      if (typeof value === 'boolean') {
        return value;
      }
      return value === 'true';
    case 'string':
      return String(value);
    default:
      return null;
  }
}

export type ValidationErrors = {
  [key: string]: { message: string } | undefined;
};

export function getValidationErrors(
  data: SafeParseReturnType<any, any>
): ValidationErrors {
  if (!data.success) {
    return data.error.errors.reduce((acc, error) => {
      const errorsWithPath = error.path.reduce(
        (acc, field) =>
          field ? { ...acc, [field]: { message: error.message } } : acc,
        {} as ValidationErrors
      );

      return { ...acc, ...errorsWithPath };
    }, {} as ValidationErrors);
  }
  return {};
}

export const createDataFieldFormSchema = (
  existingFieldIds: string[],
  currentFieldId?: string
) =>
  z
    .object({
      label: z
        .string()
        .min(1, { message: 'Label is required' })
        .max(FIELD_VALIDATION_CONSTANTS.MAX_LABEL_LENGTH, {
          message: `Label must be ${FIELD_VALIDATION_CONSTANTS.MAX_LABEL_LENGTH} characters or less`,
        }),
      id: z
        .string()
        .min(1, { message: 'ID is required' })
        .regex(/^[a-z0-9_]+$/i, {
          message: 'ID can only contain letters, numbers, and underscores',
        })
        .refine(
          (id) => !existingFieldIds.includes(id) || id === currentFieldId,
          {
            message: 'ID must be unique',
          }
        ),
      type: z.enum(['string', 'integer', 'float', 'boolean']),
      defaultValue: z.string(),
      options: z.string(),
    })
    .refine(
      (data) => {
        if (!data.type || !data.options || data.options.trim() === '')
          return true;

        const { error } = parseOptionsFromString(data.options, data.type);
        return !error;
      },
      {
        path: ['options'],
        message: 'Invalid options for selected type',
      }
    )
    .refine(
      (data) => {
        if (
          !data.options ||
          data.options.trim() === '' ||
          !data.defaultValue ||
          data.defaultValue.trim() === ''
        ) {
          return true;
        }

        const { parsedOptions } = parseOptionsFromString(
          data.options,
          data.type
        );
        if (!parsedOptions) {
          return true; // the previous refine will check for invalid options; this is just for type narrowing
        }

        const convertedDefaultValue = convertDefaultValueToType(
          data.defaultValue,
          data.type
        );
        if (convertedDefaultValue === null) {
          return true; // no default value when options are provided is okay
        }

        // if there is a default value AND options, then check if default value is in the options array
        return parsedOptions.some((option) => option === convertedDefaultValue);
      },
      {
        path: ['defaultValue'],
        message: 'Default value must be one of the provided options',
      }
    );

export const convertFormDataToFieldData = (
  data: DataFieldFormValues
): AdditionalDataInput | null => {
  const convertedDefaultValue = convertDefaultValueToType(
    data.defaultValue,
    data.type
  );

  const { parsedOptions } = parseOptionsFromString(
    data.options || '',
    data.type
  );

  switch (data.type) {
    case 'string':
      return {
        id: data.id,
        label: data.label,
        type: 'string' as const,
        default_value: convertedDefaultValue as string | null,
        options: parsedOptions as string[] | null,
      };
    case 'integer':
      return {
        id: data.id,
        label: data.label,
        type: 'integer' as const,
        default_value: convertedDefaultValue as number | null,
        options: parsedOptions as number[] | null,
      };
    case 'float':
      return {
        id: data.id,
        label: data.label,
        type: 'float' as const,
        default_value: convertedDefaultValue as number | null,
        options: parsedOptions as number[] | null,
      };
    case 'boolean':
      return {
        id: data.id,
        label: data.label,
        type: 'boolean' as const,
        default_value: convertedDefaultValue as boolean | null,
        options: parsedOptions as boolean[] | null,
      };
  }
};

export function getReorderedFields(
  fields: AdditionalDataInput[],
  oldIndex: number,
  targetIndex: number
): AdditionalDataInput[] | null {
  const field = fields[oldIndex];
  if (!field) {
    logger.info(`failed to get field with key: ${oldIndex}`);
    return null;
  }

  const newFields = [...fields];
  newFields.splice(oldIndex, 1);
  newFields.splice(targetIndex, 0, field);

  return newFields;
}
